import React, { useState } from "react";

import { LoadingState } from "../utils/enum";
import {
  fetchCompatibleEquipment,
  fetchDriverDetails,
  fetchDrivers,
  fetchEquipment,
  fetchEquipmentDetails,
  updateEquipmentAvailabilityAPI,
} from "../services/equipmentAPI";
import { Driver, Equipment, LoadData } from "../models";
import { EquipmentContextModel } from "../models/Equipment";

type EquipmentProviderProps = {
  children?: React.ReactNode;
};

export const EquipmentContext = React.createContext<EquipmentContextModel>(
  {} as EquipmentContextModel
);

export const EquipmentProvider = ({ children }: EquipmentProviderProps) => {
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [searchedEquipment, setSearchedEquipment] = useState<Equipment[]>([]);
  const [filteredEquipment, setFilteredEquipment] = useState<Equipment[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignableEquipment, setAssignableEquipment] = useState<Equipment[]>(
    []
  );
  const [searchedAssignableEquipment, setSearchedAssignableEquipment] =
    useState<Equipment[]>([]);
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  const [isFilteringActive, setIsFilteringActive] = useState(false);
  const [filterType, setFilterType] = useState<string>();
  const [filterSubType, setFilterSubType] = useState<string>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchedFilteredEquipment, setSearchedFilteredEquipment] = useState<
    Equipment[]
  >([]);
  const [filterAvailability, setFilterAvailability] = useState<boolean>();
  const [filterAssignment, setFilterAssignment] = useState<string>();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriverID, setSelectedDriverID] = useState<string>();
  const [selectedEqID, setSelectedEqID] = useState<string>();
  const [selectedDriver, setSelectedDriver] = useState<Driver>();
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment>();
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.Pending
  );
  const [loadingDetailsState, setLoadingDetailsState] = useState<LoadingState>(
    LoadingState.Pending
  );
  const [loadingAssignable, setLoadingAssignable] = useState(
    LoadingState.Pending
  );

  const listEquipment = async () => {
    try {
      setLoadingState(LoadingState.Pending);
      const response = await fetchEquipment();
      const result = response.data || [];
      const items = result.filter((item: any) => item.type !== "container");
      setEquipment(items);
      setLoadingState(LoadingState.Success);
    } catch (e) {
      setLoadingState(LoadingState.Error);
    }
  };

  const listAssignableEquipment = async (load: LoadData) => {
    try {
      setLoadingAssignable(LoadingState.Pending);
      const response = await fetchCompatibleEquipment(
        load.category,
        load.carrierSecurity
      );
      const result = response.data || [];
      setAssignableEquipment(result);
      setLoadingAssignable(LoadingState.Success);
    } catch (e) {
      setLoadingAssignable(LoadingState.Error);
    }
  };

  const searchAssignableEquipment = async (searchTerm: string) => {
    const search = searchTerm.trim().toLowerCase();

    // Implement search locally
    const searchedItems = assignableEquipment.filter((equipment) => {
      const isValid =
        equipment.lp.toLowerCase().includes(search) ||
        equipment.num.toLowerCase().includes(search) ||
        equipment.type.toLocaleLowerCase().includes(search) ||
        equipment.trailerSubType?.toLocaleLowerCase().includes(search);
      return isValid;
    });

    setSearchedAssignableEquipment(searchedItems);
  };

  const listDrivers = async () => {
    try {
      setLoadingState(LoadingState.Pending);
      const response = await fetchDrivers();
      setDrivers(response.data);
      setLoadingState(LoadingState.Success);
    } catch (e) {
      setLoadingState(LoadingState.Error);
    }
  };

  const getDriverDetails = async (driverID: string) => {
    try {
      setLoadingDetailsState(LoadingState.Pending);
      const response = await fetchDriverDetails(driverID);
      setSelectedDriver(response.data);
      setLoadingDetailsState(LoadingState.Success);
    } catch (e) {
      setLoadingDetailsState(LoadingState.Error);
    }
  };

  const getEquipmentDetails = async (equipmentID: string) => {
    try {
      setLoadingDetailsState(LoadingState.Pending);
      const data = await fetchEquipmentDetails(equipmentID);
      if (!data) {
        throw Error("Equipment not found");
      }
      setSelectedEquipment(data as Equipment);
      setLoadingDetailsState(LoadingState.Success);
    } catch (e) {
      setLoadingDetailsState(LoadingState.Error);
    }
  };

  const changeAvailability = async (eqID: string, value: boolean) => {
    await updateEquipmentAvailabilityAPI(eqID, value);
    const copiedEquipment = [...equipment];
    const eqIndex = equipment.findIndex((eq) => eq.equipmentID === eqID);
    if (eqIndex > -1) {
      copiedEquipment[eqIndex].available = value;
      setEquipment(copiedEquipment);
    }
  };

  const filterEquipment = ({
    filterAvailability,
    filterType,
    filterSubType,
    filterAssignment,
  }: {
    filterAvailability?: boolean;
    filterType?: string;
    filterSubType?: string;
    filterAssignment?: string;
  }) => {
    console.log({
      filterAvailability,
      filterType,
      filterSubType,
      filterAssignment,
    });
    if (
      !filterType &&
      filterAvailability === undefined &&
      filterSubType === undefined &&
      filterAssignment === undefined
    ) {
      setDrawerOpen(false);
      resetFilterState();
      return;
    }

    const filteredItems = equipment.filter((item) => {
      const isValidFilterAvailablity =
        filterAvailability === true || filterAvailability === false
          ? (item.available ?? false) === filterAvailability
          : true;

      const isValidFiterType = filterType ? item.type === filterType : true;
      const isValidSubType = filterSubType
        ? item.trailerSubType === filterSubType
        : true;

      const isValidFilterAssignment =
        filterAssignment === "withDriver"
          ? item.driverID !== undefined && item.driverID !== ""
          : filterAssignment === "withLoad"
          ? item.loadID !== undefined && item.loadID !== ""
          : true;

      return (
        isValidFilterAvailablity &&
        isValidFiterType &&
        isValidSubType &&
        isValidFilterAssignment &&
        item.type !== "container"
      );
    });

    setFilterAvailability(filterAvailability);
    setFilterAssignment(filterAssignment);
    setFilterType(filterType);
    setFilterSubType(filterSubType);

    if (searchTerm.length > 0) {
      searchEquipment(searchTerm);
    }

    setDrawerOpen(false);
    setFilteredEquipment(filteredItems);
    setIsFilteringActive(true);
  };

  const searchEquipment = async (searchTerm: string) => {
    const search = searchTerm.trim().toLowerCase();
    const isFilteringActive = filteredEquipment.length > 0;
    const items = isFilteringActive ? [...filteredEquipment] : [...equipment];

    // Implement search locally
    const searchedItems = items.filter((equipment) => {
      const isValid =
        equipment.lp.toLowerCase().includes(search) ||
        equipment.num.toLowerCase().includes(search) ||
        equipment.type.toLocaleLowerCase().includes(search);
      return isValid;
    });

    setSearchTerm(searchTerm);

    isFilteringActive
      ? setSearchedFilteredEquipment(searchedItems)
      : setSearchedEquipment(searchedItems);
  };

  const appendDriverToEquipment = (driver: Driver) => {
    const updatedEquipment = [...equipment];
    const eqIndex = updatedEquipment.findIndex(
      (item) => item.equipmentID === selectedEqID
    );
    if (eqIndex > -1) {
      updatedEquipment[eqIndex].driverID = driver.id;
      setEquipment(updatedEquipment);
    }
  };

  const removeDriverFromEquipment = () => {
    const updatedEquipment = [...equipment];
    const eqIndex = updatedEquipment.findIndex(
      (item) => item.equipmentID === selectedEqID
    );

    if (eqIndex > -1) {
      updatedEquipment[eqIndex].driverID = undefined; // or "" if you prefer empty string
      updatedEquipment[eqIndex].driver = undefined; // or "" if you prefer empty string

      setEquipment(updatedEquipment);
    }
  };

  const resetFilterState = () => {
    setFilterAvailability(undefined);
    setFilterAssignment(undefined);
    setFilterType(undefined);
    setFilterSubType(undefined);
    setFilteredEquipment([]);
    setSearchedFilteredEquipment([]);
    setSearchedEquipment([]);
    setDrawerOpen(false);
    setIsFilteringActive(false);
    setSearchTerm("");
  };

  // const handleToastError = ({
  //   title,
  //   description,
  //   error,
  // }: {
  //   error: any;
  //   title: string;
  //   description: string;
  // }) => {
  //   const isAxiosError = axios.isAxiosError(error);

  //   toast({
  //     title: title,
  //     position: "top",
  //     status: "error",
  //     isClosable: true,
  //     description: isAxiosError
  //       ? (error as AxiosError<any>).response?.data?.message ?? description
  //       : description,
  //   });
  // };

  const values: EquipmentContextModel = {
    drivers,
    equipment,
    searchedEquipment,
    filteredEquipment,
    loadingState,
    selectedEqID,
    selectedDriver,
    selectedDriverID,
    selectedEquipment,
    loadingDetailsState,
    filterType,
    filterSubType,
    searchTerm,
    filterAvailability,
    searchedFilteredEquipment,
    drawerOpen,
    assignableEquipment,
    assignDrawerOpen,
    loadingAssignable,
    searchedAssignableEquipment,
    isFilteringActive,
    filterAssignment,
    setFilterAssignment,
    removeDriverFromEquipment,
    appendDriverToEquipment,
    setAssignDrawerOpen,
    listAssignableEquipment,
    searchAssignableEquipment,
    setDrawerOpen,
    resetFilterState,
    setFilterAvailability,
    setFilterType,
    setFilterSubType,
    listDrivers,
    filterEquipment,
    searchEquipment,
    listEquipment,
    setLoadingState,
    setSelectedEqID,
    getDriverDetails,
    changeAvailability,
    setSelectedDriverID,
    getEquipmentDetails,
  };

  return (
    <EquipmentContext.Provider value={values}>
      {children}
    </EquipmentContext.Provider>
  );
};
